import React, { useState, useEffect } from 'react'
import { Checkbox, Select, Button, Space } from 'antd'
import { SmallSpinner } from 'portal/lib/primitives'
import PropTypes from 'prop-types'
import _ from 'lodash'

// SOURCE_MAPPING : Shorter version that the Mapping found here:
// \apps\portal\src\app\components\RecordsModules\Details\WasteManagementRoutes.tsx
export const SOURCE_MAPPING = {
  Invalid: null,
  None: null,
  PermitIdEWCCode: 'Facility and EWC code regulatory data',
  PermitId: 'Facility average using regulatory data',
  PermitTypeEWCCode:
    'Site type average across all regulatory data for specified EWC code',
  PermitType: 'Site type average across all regulatory data',
  EWCCode: 'EWC Code average across all regulatory data',
  Exemption: 'Exemption',
  Unknown: 'Unable to derive',
  CustomPermitId: 'Facility average using client-defined waste route',
  CustomPermitIdEWCCode: 'Facility and EWC code client-defined waste route'
}

function dropdownRender(
  menu,
  name,
  state,
  projectId,
  selected,
  handleOk,
  handleReset
) {
  // Skip loading/error checks for hardcoded SOURCE_MAPPING
  if (name !== 'items[].wasteManagementRoutesSource') {
    if (_.get(state.options, [projectId, name, 'loading'], true)) {
      return (
        <div style={{ textAlign: 'center' }}>
          <SmallSpinner />
        </div>
      )
    }
    if (state.options[projectId][name].error !== null) {
      return <div> An error occured while loading options.</div>
    }
  }
  return (
    <>
      {menu}
      <div style={{ padding: 8, borderTop: '1px solid #f0f0f0' }}>
        <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            size="small"
            type="text"
            disabled={selected.length === 0}
            onClick={handleReset}
          >
            Reset
          </Button>
          <Button type="primary" size="small" onClick={handleOk}>
            OK
          </Button>
        </Space>
      </div>
    </>
  )
}

export function RecordsSelect(
  name,
  state,
  fetchOptions,
  filters,
  updateFilter,
  projectId,
  tableId
) {
  const [selected, setSelected] = useState(filters?.[name] || [])

  // Pre-load options for assignedTo filter. fetchOptions (assignedToOptions) is re-created when
  // assignableUsers updates, so including it as a dep ensures we re-load once actual users arrive.
  // Calling fetchOptions only updates records.options - it does NOT change assignableUsers, so no loop.
  useEffect(() => {
    if (name === 'assignedTo') {
      fetchOptions(name)
    }
  }, [name, projectId, fetchOptions])

  const dropdownOptions = []

  // Special handling for wasteManagementRoutesSource - use SOURCE_MAPPING
  let allOptions
  if (name === 'items[].wasteManagementRoutesSource') {
    allOptions = Object.entries(SOURCE_MAPPING)
      .filter(([key, value]) => value !== null)
      .map(([key, value]) => ({
        key: key,
        label: value
      }))
  } else {
    allOptions = Array(
      ..._.get(state.options, [projectId, name, 'options'], [])
    ).filter(option => option && /\S/.test(option))
  }

  // Separate special filters (e.g., "Unassigned") from regular options
  const specialFilters = allOptions.filter(
    option => option.key === 'unassigned'
  )
  const regularOptions = allOptions.filter(
    option => option.key !== 'unassigned'
  )

  // Sort only the regular options
  const sortedRegularOptions = regularOptions.sort((a, b) =>
    a.label
      ? a.label.localeCompare(b.label, 'en', { sensitivity: 'base' })
      : a.localeCompare(b, 'en', { sensitivity: 'base' })
  )

  // Concatenate special filters first, then sorted regular options
  const sortedOptions = [...specialFilters, ...sortedRegularOptions]

  // Options can be an array of strings or objects with 'key' & 'label' properties
  sortedOptions.map(option =>
    dropdownOptions.push({
      key: option.key || option,
      value: option.key || option,
      label: option.label || option
    })
  )

  const classNameForWidth =
    name === 'items[].knownContainerType'
      ? 'knownContainerType'
      : name === 'items[].wasteCode'
        ? 'wasteCode'
        : name === 'items[].wasteManagementRoutesSource'
          ? 'wasteManagementRoutesSource'
          : name === 'products[].productGroupName'
            ? 'productGroupNames'
            : name

  const width =
    document.querySelector(`th.${classNameForWidth}`)?.offsetWidth || 200

  const handleReset = () => {
    setSelected([]) // clear temporary selection
  }

  const handleOk = () => {
    // Update filter with the current selection (which will cause a reload of the table data)
    updateFilter(name, selected)
  }

  return (
    <div style={{ width: width }}>
      <Select
        className="records-filter-select"
        name={name}
        data-testid={`${name}Filter`}
        autoClearSearchValue={false}
        maxTagCount="responsive"
        popupMatchSelectWidth={false}
        mode="multiple"
        showSearch
        allowClear
        style={{ width: '100%' }}
        options={dropdownOptions}
        optionRender={option => (
          <Checkbox checked={selected.includes(option.value)}>
            {option.label}
          </Checkbox>
        )}
        menuItemSelectedIcon={null}
        popupRender={menu =>
          dropdownRender(
            menu,
            name,
            state,
            projectId,
            selected,
            handleOk,
            handleReset
          )
        }
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
        }
        onOpenChange={isVisible => {
          if (
            isVisible &&
            name !== 'items[].wasteManagementRoutesSource' &&
            _.get(state.options, [projectId, name]) === undefined
          ) {
            fetchOptions(name)
          }
          if (!isVisible) {
            updateFilter(name, selected) // Ensure filter is updated when dropdown is closed, even if no change was made to the selection
          }
        }}
        getPopupContainer={() => document.getElementById(tableId)}
        value={selected}
        onChange={setSelected}
      />
    </div>
  )
}

RecordsSelect.propTypes = {
  name: PropTypes.string,
  state: PropTypes.object,
  fetchOptions: PropTypes.func,
  filters: PropTypes.object,
  updateFilter: PropTypes.func
}
